import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { categoryService } from "@/services/altingross";

// ─── Thunks ─────────────────────────────────────────────────────────────────
export const fetchCategories = createAsyncThunk(
  "categories/fetchList",
  async ({ page = 1, size = 20, field, value, operator } = {}, { rejectWithValue }) => {
    try {
      const res = await categoryService.getList({ page, size, field, value, operator });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const createCategory = createAsyncThunk(
  "categories/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await categoryService.create(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  "categories/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await categoryService.update(id, data);
      return { id, ...res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "categories/delete",
  async (id, { rejectWithValue }) => {
    try {
      await categoryService.deleteById(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────
const categoriesSlice = createSlice({
  name: "categories",
  initialState: {
    list: [],
    pagination: { page: 1, size: 20, count: 0, totalPages: 1, hasNext: false, hasPrev: false },
    loading: false,
    error: null,
    savingId: null,
    deletingId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchCategories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        const { items, page, size, count, totalPages, hasNext, hasPrev } = action.payload;
        state.list = items ?? [];
        state.pagination = { page, size, count, totalPages, hasNext, hasPrev };
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // createCategory — reload triggered from component
      .addCase(createCategory.pending, (state) => { state.savingId = "__new__"; })
      .addCase(createCategory.fulfilled, (state) => { state.savingId = null; })
      .addCase(createCategory.rejected, (state) => { state.savingId = null; })

      // updateCategory
      .addCase(updateCategory.pending, (state, action) => { state.savingId = action.meta.arg.id; })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.savingId = null;
        const idx = state.list.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.list[idx] = { ...state.list[idx], ...action.payload };
      })
      .addCase(updateCategory.rejected, (state) => { state.savingId = null; })

      // deleteCategory
      .addCase(deleteCategory.pending, (state, action) => { state.deletingId = action.meta.arg; })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.deletingId = null;
        state.list = state.list.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteCategory.rejected, (state) => { state.deletingId = null; });
  },
});

export default categoriesSlice.reducer;
